import { useState, useCallback, useEffect, useRef } from 'react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FieldValues, Control } from 'react-hook-form';

import { debounce } from 'lodash';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SelectOption = { value: string; label: string };

interface SelectFieldProps {
  control: unknown;
  name: string;
  label?: string;
  placeholder: string;
  options: SelectOption[];
  onChangeValue?: (value: string) => void;
  onSearch?: (searchTerm: string) => void; // Optionally pass search function from parent if needed
  disabled?: boolean;
  isMandatory?: boolean;
}

export const SelectField = ({
  control,
  name,
  label,
  placeholder,
  options,
  onChangeValue,
  onSearch,
  disabled = false,
  isMandatory,
}: SelectFieldProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null); // Create a ref for the input

  // Limit the options displayed to 10
  const limitedOptions = filteredOptions.slice(0, 10);
  // Debounce the search input to avoid too many API calls
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (onSearch) {
        onSearch(term);
      } else {
        setFilteredOptions(
          options.filter((option) =>
            option?.label?.toLowerCase()?.includes(term?.toLowerCase())
          )
        );
      }
    }, 300),
    [options, onSearch]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    inputRef.current?.focus(); // Auto-focus the input when the component mounts
  }, []);

  return (
    <FormField
      control={control as Control<FieldValues>}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          {label && (
            <FormLabel>
              {label}{' '}
              {isMandatory && (
                <span
                  className={
                    fieldState.error?.message ? 'text-red-500' : 'text-inherit'
                  }
                >
                  *
                </span>
              )}
            </FormLabel>
          )}
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              onChangeValue?.(value);
            }}
            defaultValue={field.value}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    <span className="text-gray-500">{placeholder}</span>
                  }
                />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <div className="p-2">
                <Input
                  ref={inputRef} // Attach the ref to the input
                  placeholder="Type to search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={disabled}
                />
              </div>
              {limitedOptions.length > 0 ? (
                limitedOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))
              ) : (
                <div className="p-2 text-muted-foreground">
                  No results found
                </div>
              )}
            </SelectContent>
          </Select>
          <FormMessage className="opacity-70" />
        </FormItem>
      )}
    />
  );
};
